import type { ClientBuilder } from "api-types/builders/clientBuilderTypes";
import type { BuilderAccount } from "api-types/common/responseTypes";
import type {
  ClientHomePlan,
  ClientHomePlanHydrated,
  ClientHomePlanHydratedNoDoc,
} from "api-types/homePlans/homePlanResponses";
import type { HomePlanStatus } from "api-types/homePlans/homePlanTypes";
import type { ClientLocationPlan } from "api-types/locationPlans/locationPlansResponses";
import type { ClientLocationHydrated } from "api-types/locations/locationsResponses";
import { WEB_API_BASE_PATH } from "common/constants";
import { assertIsNotUndefined, ensureDefined } from "common/utils/assertOfType";
import { objectTypedKeys } from "common/utils/objectTypedKeys";
import { assertValidatesSchemaTODO } from "doc/schema/docSchemaAssertion";
import getLotNumberDisplay from "lib/components/builders/communities/plans/getLotNumberDisplay";
import type { BuilderModalStep } from "lib/components/builders/communities/plans/modals/BuilderModalStepped";
import BuilderModalStepped from "lib/components/builders/communities/plans/modals/BuilderModalStepped";
import EditHomePlanModal from "lib/components/builders/communities/plans/modals/CreateOrUpdateHomePlanModal";
import SectionHeader from "lib/components/builders/communities/plans/SectionHeader";
import useHomePlanEvents from "lib/components/builders/communities/utils/useHomePlanEvents";
import { HOME_PLAN_LOCKING_IMPLICATIONS, HOME_PLAN_UNLOCKING_IMPLICATIONS } from "lib/constants/messages";
import { getBuilderLotHomePlansURL } from "lib/endpointHelpers";
import { useFetch } from "lib/fetcher";
import useDownloadState from "lib/hooks/useDownloadState";
import { homePlanShowroomPath } from "lib/routeHelpers";
import { userCanEditHomePlan } from "lib/security/client";
import { useUserContext } from "lib/state/UserContextProvider";
import { getHomePlanHydrated, putHomePlanChangeStatus } from "lib/storage/homePlanStorage";
import type { ReactNode } from "react";
import { useState } from "react";
import Button from "ui/buttons/Button";
import Checklist from "ui/components/Checklist";
import Link from "ui/components/Link";
import cx from "ui/cx";
import Checkbox from "ui/forms/CheckboxOld";
import useAppContext from "ui/hooks/useAppContext";
import IconHouse from "ui/icons/IconHouse";
import IconLineArrow from "ui/icons/IconLineArrow";
import IconPhaseConstruction from "ui/icons/IconPhaseConstruction";
import IconPhasePlanPrep from "ui/icons/IconPhasePlanPrep";
import IconPhasePreConstruction from "ui/icons/IconPhasePreConstruction";
import TwoByStatTable from "ui/tables/TwoByStatTable";
import { Message } from "ui/text/Message";
import styles from "./PlanPhases.module.scss";

export const DOWNLOAD_SALES_BROCHURE_TASK = "Download Sales Brochure.";
export const HOME_CREDIT_REQUIRED_TASK = "Home credit required.";
export const NO_ADDITIONAL_CREDIT_TASK = "No additional credit required.";

export const homePlanPhases: {
  [key in HomePlanStatus]: { name: string; tasks: string[] };
} = {
  PREP: {
    name: "Plan Preparation",
    tasks: ["Configure options.", DOWNLOAD_SALES_BROCHURE_TASK, "Share customer link."],
  },
  PRE_CONSTRUCTION: {
    name: "Pre-Construction",
    tasks: [
      HOME_CREDIT_REQUIRED_TASK,
      "Download plan set (without watermark).",
      "Download Home Plan data.",
      "Download DXF file.",
    ],
  },
  CONSTRUCTION: {
    name: "Construction",
    tasks: [NO_ADDITIONAL_CREDIT_TASK, "Indicates construction has commenced."],
  },
};

/** Drops the credit checklist items when the legacy credit ledger is retired for the builder. */
export function filterCreditTasks(tasks: string[], creditsEnabled: boolean): string[] {
  if (creditsEnabled) {
    return tasks;
  }
  return tasks.filter((task) => task !== HOME_CREDIT_REQUIRED_TASK && task !== NO_ADDITIONAL_CREDIT_TASK);
}

export function getPhaseTasks(homePlan: ClientHomePlanHydrated, creditsEnabled: boolean): string[] {
  const skipHomeCredit = homePlan.category === "testing" && homePlan.planStatus === "PRE_CONSTRUCTION";

  return filterCreditTasks(homePlanPhases[homePlan.planStatus].tasks, creditsEnabled).filter((task) => {
    if (skipHomeCredit && task === HOME_CREDIT_REQUIRED_TASK) {
      return false;
    }
    // Full-building multi-unit plans do not support sales brochures.
    if (homePlan.isMultiUnit && task === DOWNLOAD_SALES_BROCHURE_TASK) {
      return false;
    }
    return true;
  });
}

interface UpdatePhaseModalProps {
  open: boolean;
  onClose: () => void;
  homePlan: ClientHomePlanHydrated;
  newPhase: HomePlanStatus | null;
  builder: ClientBuilder;
  location: ClientLocationHydrated | undefined;
  updateHomePlan: ((hp: ClientHomePlanHydrated) => void) | undefined;
}

function UpdatePhaseModal({
  homePlan,
  location,
  open,
  onClose,
  updateHomePlan,
  newPhase,
  builder,
}: UpdatePhaseModalProps) {
  const [shouldLock, setShouldLock] = useState(true);
  const [errorState, setErrorState] = useState(false);
  const [{ progress, error }, dispatch] = useDownloadState();
  const { setUserNotification } = useAppContext();
  const { isSuperAdmin, enabledFeatures } = useUserContext();
  const creditsEnabled = !!enabledFeatures.legacyHomePlanCredits;
  const { data: builderAccount } = useFetch<BuilderAccount>(
    creditsEnabled ? `${WEB_API_BASE_PATH}/builders/${builder?.id}/account` : "",
    [builder?.id, creditsEnabled],
  );
  const { data: lotHomePlans } = useFetch<ClientHomePlan[]>(
    location && homePlan.lotId
      ? getBuilderLotHomePlansURL({
          builderId: builder.id,
          locationId: location.id,
          lotId: homePlan.lotId,
        })
      : "",
    [homePlan?.lotId, location?.id],
  );
  const hasCreditBeenSpent = (builderAccount?.credits ?? []).some((credit) => credit.homePlanId === homePlan.id);
  const [shouldCharge, setShouldCharge] = useState(homePlan?.category !== "testing" && !hasCreditBeenSpent);
  const sendHomePlanEvent = useHomePlanEvents(homePlan);

  const homePlanLocked = !!homePlan?.lockedAt;

  if (!newPhase) {
    return null;
  }

  const onModalClose = () => {
    setShouldCharge(homePlan?.category !== "testing" && !hasCreditBeenSpent);
    setShouldLock(true);
    dispatch({ type: "RESET" });
    setErrorState(false);
    onClose();
  };

  const handleSubmit = async (setIsSaving?: (isSaving: boolean) => void) => {
    assertIsNotUndefined(builder);
    // Only pass shouldLock if the plan is already locked (lockingStep is shown)
    const lockParam = homePlanLocked ? shouldLock : undefined;
    const [response, msg] = await putHomePlanChangeStatus(builder.id, homePlan.id, newPhase, shouldCharge, lockParam);
    if (msg) {
      setUserNotification(msg);
      setIsSaving?.(false);
      return;
    }

    if (!error) {
      const clearedPlanCount = response?.clearedHomePlansIdOnSameLot?.length;
      sendHomePlanEvent("config:moved_homeplan_phase");
      setUserNotification({
        messageType: "success",
        message: `Moved Home Plan to ${homePlanPhases[newPhase].name}.${
          clearedPlanCount
            ? ` Cleared ${clearedPlanCount} Home Plan${clearedPlanCount > 1 ? "s" : ""} from the same lot.`
            : ""
        }`,
      });

      const [homePlanHydrated, err] = await getHomePlanHydrated(builder.id, homePlan.id);
      if (err) {
        setUserNotification(err);
      } else {
        assertIsNotUndefined(updateHomePlan);
        updateHomePlan(ensureDefined(homePlanHydrated));
      }

      onModalClose();
    }
  };

  const header = `Move Home Plan to ${homePlanPhases[newPhase].name}`;
  const total = (builderAccount?.credits ?? []).map(({ amount }) => amount).reduce((sum, i) => sum + i, 0);

  const steps: BuilderModalStep[] = [
    {
      header,
      subheader: "Review the checklist for this phase.",
      body: <Checklist items={getPhaseTasks(homePlan, creditsEnabled)} />,
      submittable: true,
      submittingText: "Moving Home Plan...",
      submitButtonText: "Move Home Plan",
    },
  ];

  const preConstructionStep: BuilderModalStep = {
    header,
    subheader: "Review Plan Set purchase.",
    body: (
      <>
        <TwoByStatTable>
          <table style={{ width: "100%" }}>
            <tbody>
              <tr>
                <td>Current Credit Balance:</td>
                <td style={{ textAlign: "right", fontWeight: 500 }}>{total.toLocaleString()}</td>
              </tr>
              <tr>
                <td>Plan Set Cost (Credits):</td>
                <td style={{ color: "#F55D58", textAlign: "right", fontWeight: 500 }}>{shouldCharge ? -1 : 0}</td>
              </tr>
              <tr style={{ borderTop: "1px solid rgba(1,1,1,.2)" }}>
                <td>Remaining Credit Balance:</td>
                <td style={{ textAlign: "right", fontWeight: 500 }}>
                  {(total - (shouldCharge ? 1 : 0)).toLocaleString()}
                </td>
              </tr>
              {isSuperAdmin && (
                <tr>
                  <td>
                    <Checkbox
                      label="Use a credit for this move"
                      checked={shouldCharge}
                      onChange={() => setShouldCharge(!shouldCharge)}
                      disabled={homePlan?.category === "testing" || hasCreditBeenSpent}
                      title="Home Plans categorized as 'Testing' do not cost a Credit to move."
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </TwoByStatTable>
      </>
    ),
    submittable: shouldCharge ? total > 0 : true,
    submittingText: "Moving Home Plan...",
    submitButtonText: "Move Home Plan",
  };

  const homesToRemove = lotHomePlans?.filter((hp) => hp.id !== homePlan.id)?.map((hp) => hp.planName) ?? [];
  const homesToRemoveNonNull = homesToRemove.filter((e) => e !== null && e !== undefined);
  const lotHomePlansStep: BuilderModalStep = {
    header,
    subheader: `Moving this Home Plan to ${
      homePlanPhases[newPhase].name
    } will remove other plans from Lot ${getLotNumberDisplay(homePlan?.lot)}.`,
    body: (
      <>
        Home Plans that will be removed from this lot:
        <Checklist items={homesToRemoveNonNull} icon={<IconHouse size={18} color="stone" />} />
      </>
    ),
    submittable: true,
    submittingText: "Moving Home Plan...",
    submitButtonText: "Move Home Plan",
  };

  const lockingLanguage = newPhase === "PREP" ? HOME_PLAN_UNLOCKING_IMPLICATIONS : HOME_PLAN_LOCKING_IMPLICATIONS;

  const lockingStep: BuilderModalStep = {
    header,
    subheader: `Do you want to ${newPhase === "PREP" ? "unlock" : "lock"} this Home Plan?`,
    body: (
      <>
        {newPhase === "PREP" ? "Unlocking" : "Locking"} this Home Plan will do the following:
        <Checklist items={lockingLanguage} />
        <br />
        <Checkbox
          label={newPhase === "PREP" ? "Unlock Home Plan" : "Lock Home Plan"}
          checked={shouldLock}
          onChange={() => setShouldLock(!shouldLock)}
        />
      </>
    ),
    submittable: true,
    submittingText: "Moving Home Plan...",
    submitButtonText: "Move Home Plan",
  };

  // Kick other plans off the lot if moving past PREP
  if (["PRE_CONSTRUCTION", "CONSTRUCTION"].includes(newPhase) && lotHomePlans && lotHomePlans.length > 1) {
    steps.push(lotHomePlansStep);
  }

  // Only allow for plans that are already locked to be unlocked
  if (["PRE_CONSTRUCTION", "CONSTRUCTION"].includes(newPhase) && homePlanLocked) {
    steps.push(lockingStep);
  }

  if (newPhase === "PRE_CONSTRUCTION" && creditsEnabled) {
    steps.push(preConstructionStep);
  }

  return (
    <BuilderModalStepped
      modalProps={{ open, onClose: onModalClose, size: "small" }}
      steps={steps}
      onSubmit={handleSubmit}
      progress={progress}
      error={errorState ? "Error generating Plan Set" : undefined}
    />
  );
}

interface PlanPhasesProps {
  homePlan: ClientHomePlanHydrated;
  builder: ClientBuilder;
  locationPlan: ClientLocationPlan | undefined;
  updateHomePlan: ((hp: ClientHomePlanHydratedNoDoc) => void) | undefined;
  location: ClientLocationHydrated | undefined;
}

export default function PlanPhases({ location, homePlan, builder, updateHomePlan }: PlanPhasesProps) {
  const [newPhase, setNewPhase] = useState<HomePlanStatus | null>(null);
  const [openEditModal, setOpenEditModal] = useState(false);
  const phaseIndex = Object.keys(homePlanPhases).indexOf(homePlan.planStatus);
  const homePlanPhaseNames = Object.keys(homePlanPhases);
  const { hasPermission, user, enabledFeatures } = useUserContext();
  const userCanEditPlan = userCanEditHomePlan({ hasPermission, user }, homePlan, location);
  const isFullBuildingPlan = !!homePlan.isMultiUnit;

  return (
    <>
      <SectionHeader title={isFullBuildingPlan ? "Multi-Unit Home Plan Phases" : "Plan Phases"} />
      {isFullBuildingPlan && (
        <p className={styles.introText}>Construction phases for the whole building are managed from this plan.</p>
      )}
      <div className={styles.wrapper}>
        <div className="plan-phases-header">
          {objectTypedKeys(homePlanPhases).map((p: HomePlanStatus) => {
            const pastPhase = Object.keys(homePlanPhases).indexOf(p) < phaseIndex;

            let phaseIcon: ReactNode;

            switch (p) {
              case "PREP": {
                phaseIcon = <IconPhasePlanPrep size={16} />;
                break;
              }
              case "PRE_CONSTRUCTION": {
                phaseIcon = <IconPhasePreConstruction size={16} />;
                break;
              }
              case "CONSTRUCTION": {
                phaseIcon = <IconPhaseConstruction size={16} />;
                break;
              }
            }

            return (
              <div
                key={p}
                className={cx({
                  styles,
                  phase: true,
                  pastPhase,
                  phaseActive: p === homePlan.planStatus,
                  enabled: pastPhase && hasPermission("CHANGE_HOME_PLAN_STATUS"),
                })}
                onClick={
                  hasPermission("CHANGE_HOME_PLAN_STATUS")
                    ? () => {
                        if (pastPhase) {
                          assertValidatesSchemaTODO<HomePlanStatus>(p);
                          setNewPhase(p);
                        }
                      }
                    : undefined
                }
              >
                {phaseIcon}
                {homePlanPhases[p].name}
              </div>
            );
          })}
        </div>
        <div className="phase-section">
          <div className="rec-tasks">
            <div className="rec-tasks-header">Phase Tasks</div>
            <Checklist items={getPhaseTasks(homePlan, !!enabledFeatures.legacyHomePlanCredits)} />
          </div>
        </div>
        {userCanEditPlan && !isFullBuildingPlan && (
          <div className="phase-section">
            <Link href={homePlanShowroomPath(ensureDefined(builder).id, homePlan.id)} className={styles.configureLink}>
              {!homePlan.lockedAt ? "Configure " : "View "} Home Plan <IconLineArrow size={18} />
            </Link>
          </div>
        )}
      </div>
      <div className={styles.phaseActions}>
        {!homePlan.lotId && !homePlan.isMultiUnit && (
          <Message messageType="warning" variant="inverted">
            <span>
              Home plan is not assigned to a lot
              {userCanEditPlan && !homePlan.lockedAt ? (
                <>
                  : <Link onClick={() => setOpenEditModal(true)}>Add Lot</Link>
                </>
              ) : null}
              .
            </span>
          </Message>
        )}
        {phaseIndex + 1 < homePlanPhaseNames.length && hasPermission("CHANGE_HOME_PLAN_STATUS") && (
          <Button
            title="You do not have the permission to move this Home Plan to a new phase."
            disabled={!hasPermission("CHANGE_HOME_PLAN_STATUS")}
            onClick={() => setNewPhase(homePlanPhaseNames[phaseIndex + 1] as HomePlanStatus)}
          >
            Move to {homePlanPhases[homePlanPhaseNames[phaseIndex + 1]].name}
          </Button>
        )}
        {openEditModal && (
          <EditHomePlanModal
            builderId={ensureDefined(builder).id}
            locationId={location?.id ?? undefined}
            homePlan={homePlan}
            onClose={() => setOpenEditModal(false)}
            onSuccess={updateHomePlan}
          />
        )}
        {homePlan && newPhase !== null && (
          <UpdatePhaseModal
            open
            onClose={() => setNewPhase(null)}
            homePlan={homePlan}
            location={location}
            builder={builder}
            updateHomePlan={updateHomePlan}
            newPhase={newPhase}
          />
        )}
      </div>
    </>
  );
}
